/**
 * The drawer's sections, in the order spec 02-orders lists them.
 *
 * Payment, Fulfilment, Items, Customer, Notes and activity, Flags. Each one renders a
 * branch of `GET /orders/:id/detail` and nothing else: the matcher note is the matcher's
 * words, the print acks are the print agent's timestamps, the timeline is the merge the
 * server built. There is no section here that says something the server did not.
 *
 * A section whose branch came back empty says so in a sentence. It never renders a zero in
 * place of an unknown, and it never renders nothing at all, because a blank section reads
 * as "nothing happened" when it means "we could not read it".
 *
 * Every action a section can take is passed in. The section itself knows how to lay a fact
 * out and nothing about how to change one.
 */
import type { ReactNode } from 'react';
import type { Address, TimelineKind } from '@elutide/shared';
import { Pill } from '@/components';
import { countdown, formatCount, formatMoney, fullTimestamp, relativeAge } from '@/lib/format';
import { MATCHER_LABEL, type OrderDrawerDetailView as OrderDrawerDetail } from '../types';
import { safeTrackingUrl } from '../cells';

export function Section({
  title,
  id,
  action,
  children,
}: {
  title: string;
  id: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="ord-sec" id={`order-section-${id}`} aria-labelledby={`order-section-${id}-h`}>
      <header className="ord-sec__head">
        <h3 className="o-section-title" id={`order-section-${id}-h`}>
          {title}
        </h3>
        {action}
      </header>
      <div className="ord-sec__body">{children}</div>
    </section>
  );
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="o-kv">
      <span className="o-kv__key">{label}</span>
      <span className="o-kv__value">{children}</span>
    </div>
  );
}

/** A timestamp a person can read, with the exact instant on hover. */
export function When({ at, now }: { at: string | null; now: number }) {
  if (!at) return <span className="ord-sub">not yet</span>;
  return <span title={fullTimestamp(at)}>{relativeAge(at, now)}</span>;
}

const MATCHER_WORDS: Readonly<Record<OrderDrawerDetail['payment']['matcher_state'], string>> = {
  auto_matched: MATCHER_LABEL.auto,
  sms_confirmed: MATCHER_LABEL.sms,
  manual: MATCHER_LABEL.manual,
  none: 'Not matched',
};

/* ------------------------------------------------------------------ */
/* 1. Payment                                                          */
/* ------------------------------------------------------------------ */

export function PaymentSection({
  detail,
  now,
  actions,
}: {
  detail: OrderDrawerDetail;
  now: number;
  actions?: ReactNode;
}) {
  const p = detail.payment;
  /**
   * A packer's payment block is the state and the deadline only (`redact.ts`): no method,
   * no reference, no amounts, no payer. Each of those facts is skipped when its field is
   * absent. `null` still means "known to be none" and renders as such.
   */
  const expected = p.expected_cents ?? null;
  const received = p.received_cents ?? null;
  const variance = expected !== null && received !== null && expected !== received;

  return (
    <Section title="Payment" id="payment" action={actions}>
      {p.method !== undefined ? (
        <Fact label="Method">
          {p.method_raw && p.method_raw !== p.method ? `${p.method} (${p.method_raw})` : p.method}
        </Fact>
      ) : null}
      {p.reference !== undefined || p.transaction_id !== undefined ? (
        <Fact label="Reference">
          <span className="o-mono">{p.reference ?? p.transaction_id ?? 'none'}</span>
        </Fact>
      ) : null}
      <Fact label="Paid at">
        <When at={p.paid_at} now={now} />
      </Fact>
      <Fact label="Matcher">
        <span className="ord-badge ord-badge--matcher">{MATCHER_WORDS[p.matcher_state]}</span>
      </Fact>

      {p.matcher_note ? <p className="ord-note ord-note--quote">{p.matcher_note}</p> : null}

      {expected !== null || received !== null ? (
        <Fact label="Expected vs received">
          <span className={variance ? 'ord-variance' : undefined}>
            {formatMoney(expected, { showCents: true })} expected,{' '}
            {formatMoney(received, { showCents: true })} received
            {p.payer_name ? ` from ${p.payer_name}` : ''}
          </span>
        </Fact>
      ) : null}

      {p.autocancel_at ? (
        <Fact label="Auto-cancel">
          <span
            className="ord-countdown"
            data-late={Date.parse(p.autocancel_at) <= now ? 'true' : 'false'}
            title={fullTimestamp(p.autocancel_at)}
          >
            {Date.parse(p.autocancel_at) <= now
              ? `overdue ${countdown(p.autocancel_at, now)}`
              : `in ${countdown(p.autocancel_at, now)}`}
          </span>
        </Fact>
      ) : null}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* 2. Fulfilment                                                       */
/* ------------------------------------------------------------------ */

export function FulfilmentSection({
  detail,
  now,
  photos,
  tracking,
}: {
  detail: OrderDrawerDetail;
  now: number;
  /** The photo strip. Passed in so the lightbox owns its own state. */
  photos?: ReactNode;
  tracking?: ReactNode;
}) {
  const f = detail.fulfilment;
  const acks: readonly { label: string; at: string | null; reprints: number }[] = [
    { label: 'Packing slip', at: f.print_acks.packing_slip_at, reprints: f.print_acks.packing_slip_reprints },
    { label: 'Certificate', at: f.print_acks.coa_at, reprints: f.print_acks.coa_reprints },
    { label: 'Shipping label', at: f.print_acks.label_at, reprints: f.print_acks.label_reprints },
  ];

  return (
    <Section title="Fulfilment" id="fulfilment">
      {acks.map((ack) => (
        <Fact key={ack.label} label={ack.label}>
          <When at={ack.at} now={now} />
          {ack.reprints > 0 ? (
            <span className="ord-sub"> . {formatCount(ack.reprints)} reprints</span>
          ) : null}
        </Fact>
      ))}

      <Fact label="Packed">
        <When at={f.packed_at} now={now} />
        {f.packed_by ? <span className="ord-sub"> by {f.packed_by}</span> : null}
      </Fact>

      {photos}

      <Fact label="Tracking">
        {f.tracking_number ? (
          safeTrackingUrl(f.tracking_url) ? (
            <a
              className="ord-track"
              href={safeTrackingUrl(f.tracking_url) as string}
              target="_blank"
              rel="noreferrer noopener"
            >
              {f.carrier.toUpperCase()} {f.tracking_number}
            </a>
          ) : (
            <span className="o-mono">
              {f.carrier.toUpperCase()} {f.tracking_number}
            </span>
          )
        ) : (
          <span className="ord-sub">none</span>
        )}
      </Fact>
      {tracking}

      <Fact label="Shipped">
        <When at={f.shipped_at} now={now} />
      </Fact>
      <Fact label="Delivered">
        <When at={f.delivered_at} now={now} />
      </Fact>
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* 3. Items                                                            */
/* ------------------------------------------------------------------ */

export function ItemsSection({
  detail,
  actions,
}: {
  detail: OrderDrawerDetail;
  actions?: ReactNode;
}) {
  const items = detail.items;
  /**
   * A packer's items block is the lines as `{id, sku, name, qty}` and the coupon codes
   * (`redact.ts`): no unit price, no line total, no totals underneath. The table drops its
   * two money columns and the totals block is skipped, so what remains is a pick list.
   */
  const totals =
    items.subtotal_cents !== undefined &&
    items.discount_cents !== undefined &&
    items.shipping_cents !== undefined &&
    items.tax_cents !== undefined &&
    items.total_cents !== undefined
      ? {
          subtotal: items.subtotal_cents,
          discount: items.discount_cents,
          shipping: items.shipping_cents,
          tax: items.tax_cents,
          total: items.total_cents,
          refunded: items.refunded_cents ?? 0,
        }
      : null;
  const lineMoney = items.lines.some((line) => line.unit_price_cents !== undefined);
  const residual = totals
    ? totals.total - (totals.subtotal - totals.discount + totals.shipping + totals.tax)
    : 0;

  return (
    <Section title="Items" id="items" action={actions}>
      {items.lines.length === 0 ? (
        <p className="ord-sub">This order has no lines.</p>
      ) : (
        <table className="ord-lines">
          <caption className="o-visually-hidden">Order lines</caption>
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col" className="o-cell--right">
                Qty
              </th>
              {lineMoney ? (
                <>
                  <th scope="col" className="o-cell--right">
                    Unit
                  </th>
                  <th scope="col" className="o-cell--right">
                    Line
                  </th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {items.lines.map((line) => (
              <tr key={line.line_id}>
                <td>
                  <span className="ord-name">{line.name}</span>
                  {line.sku ? <span className="ord-sub o-mono">{line.sku}</span> : null}
                </td>
                <td className="o-cell--right">{line.qty}</td>
                {lineMoney ? (
                  <>
                    <td className="o-cell--right">
                      {line.unit_price_cents !== undefined
                        ? formatMoney(line.unit_price_cents, { showCents: true })
                        : null}
                    </td>
                    <td className="o-cell--right">
                      {line.line_total_cents !== undefined
                        ? formatMoney(line.line_total_cents, { showCents: true })
                        : null}
                      {line.discount_cents !== undefined && line.discount_cents > 0 ? (
                        <span className="ord-sub">
                          less {formatMoney(line.discount_cents, { showCents: true })}
                        </span>
                      ) : null}
                    </td>
                  </>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {items.coupon_codes.length > 0 ? (
        <Fact label="Coupons">
          {items.coupon_codes.map((code) => (
            <span key={code} className="ord-badge ord-badge--matcher">
              {code}
            </span>
          ))}
        </Fact>
      ) : null}

      {totals ? (
        <>
          <Fact label="Subtotal">{formatMoney(totals.subtotal, { showCents: true })}</Fact>
          {totals.discount > 0 ? (
            <Fact label="Discount">less {formatMoney(totals.discount, { showCents: true })}</Fact>
          ) : null}
          <Fact label="Shipping">{formatMoney(totals.shipping, { showCents: true })}</Fact>
          {totals.tax > 0 ? (
            <Fact label="Tax">{formatMoney(totals.tax, { showCents: true })}</Fact>
          ) : null}
          {residual !== 0 ? (
            <Fact label="Not itemised">
              {/*
                Woo can carry a total that the lines, the discount, the shipping and the tax do
                not add up to: a coupon applied at the cart, a manual adjustment, a fee. Showing
                the gap and saying it is a gap is honest. Silently printing a subtotal and a
                total that do not reconcile reads as arithmetic nobody can trust, which is the
                thing this whole page was rebuilt to stop.
              */}
              <span className="ord-variance">
                {residual < 0 ? 'less ' : ''}
                {formatMoney(Math.abs(residual), { showCents: true })}
              </span>
              <span className="ord-sub">not attributed to a line, a coupon, shipping or tax</span>
            </Fact>
          ) : null}
          <Fact label="Total">
            <strong>{formatMoney(totals.total, { showCents: true })}</strong>
          </Fact>
          {totals.refunded > 0 ? (
            <Fact label="Refunded">
              <span className="ord-refunded">{formatMoney(totals.refunded, { showCents: true })}</span>
            </Fact>
          ) : null}
        </>
      ) : null}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* 4. Customer                                                         */
/* ------------------------------------------------------------------ */

function AddressLines({ address }: { address: Address | null }) {
  if (!address) return <span className="ord-sub">none on file</span>;
  const name = `${address.first_name} ${address.last_name}`.trim();
  return (
    <span className="ord-address">
      {[
        name,
        address.company,
        address.address_1,
        address.address_2,
        `${address.city}, ${address.state} ${address.postcode}`,
        address.country === 'US' ? null : address.country,
      ]
        .filter((line): line is string => Boolean(line && line.trim() !== ''))
        .map((line) => (
          <span key={line}>{line}</span>
        ))}
    </span>
  );
}

export function CustomerSection({
  detail,
  actions,
  creditActions,
}: {
  detail: OrderDrawerDetail;
  actions?: ReactNode;
  creditActions?: ReactNode;
}) {
  const c = detail.customer;
  return (
    <Section title="Customer" id="customer" action={actions}>
      {/*
        Email, phone, billing, the lifetime block and the rewards balance are absent for a
        packer (redact.ts, CONTRACTS.md 3.6). Absent skips the fact; null still says none.
      */}
      <Fact label="Name">{c.name}</Fact>
      {c.email !== undefined ? (
        <Fact label="Email">{c.email ?? <span className="ord-sub">none</span>}</Fact>
      ) : null}
      {c.phone !== undefined ? (
        <Fact label="Phone">{c.phone ?? <span className="ord-sub">none</span>}</Fact>
      ) : null}
      <Fact label="Shipping">
        <AddressLines address={c.shipping} />
      </Fact>
      {c.billing !== undefined ? (
        <Fact label="Billing">
          <AddressLines address={c.billing} />
        </Fact>
      ) : null}
      {c.lifetime ? (
        <Fact label="Lifetime">
          {formatCount(c.lifetime.orders)} paid orders,{' '}
          {formatMoney(c.lifetime.spend_cents)} net of refunds
        </Fact>
      ) : null}
      {c.rr_balance_cents !== undefined ? (
        <Fact label="Research Rewards">
          {c.rr_balance_cents === null ? (
            <span className="ord-sub">no ledger for this customer</span>
          ) : (
            formatMoney(c.rr_balance_cents, { showCents: true })
          )}
        </Fact>
      ) : null}
      {creditActions}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* 5. Notes and activity                                               */
/* ------------------------------------------------------------------ */

const KIND_GLYPH: Readonly<Record<TimelineKind, string>> = {
  note: 'N',
  audit: 'A',
  print: 'P',
  pack: 'K',
  status: 'S',
  matcher: 'M',
  payment: '$',
  shipment: 'T',
};

const KIND_WORD: Readonly<Record<TimelineKind, string>> = {
  note: 'Note',
  audit: 'Ops action',
  print: 'Print',
  pack: 'Pack',
  status: 'Status',
  matcher: 'Matcher',
  payment: 'Payment',
  shipment: 'Shipment',
};

export function ActivitySection({
  detail,
  now,
  composer,
}: {
  detail: OrderDrawerDetail;
  now: number;
  composer?: ReactNode;
}) {
  const entries = detail.notes_and_activity;
  return (
    <Section title="Notes and activity" id="activity">
      {composer}
      {entries.length === 0 ? (
        <p className="ord-sub">
          Nothing recorded against this order yet, or the WordPress read did not answer.
        </p>
      ) : (
        <ol className="ord-timeline" role="list">
          {entries.map((entry) => (
            <li key={entry.id} className="ord-timeline__item" data-kind={entry.kind}>
              <span className="ord-timeline__glyph" aria-hidden="true">
                {KIND_GLYPH[entry.kind]}
              </span>
              <div className="ord-timeline__body">
                <span className="ord-timeline__meta">
                  <span className="ord-badge">{KIND_WORD[entry.kind]}</span>
                  {entry.visibility === 'customer' ? (
                    <Pill tone="info">Customer visible</Pill>
                  ) : null}
                  <span className="ord-sub" title={fullTimestamp(entry.at)}>
                    {relativeAge(entry.at, now)} . {entry.actor}
                  </span>
                </span>
                <p className="ord-note">{entry.text}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* 6. Flags                                                            */
/* ------------------------------------------------------------------ */

const FLAG_WORD: Readonly<Record<string, string>> = {
  compliance_hold: 'Compliance hold',
  address_problem: 'Address flagged',
  attempt_cluster: 'Attempt cluster',
  payment_variance: 'Payment variance',
  unmapped_payment_method: 'Unmapped payment method',
  writeback_dead: 'Write-back failed',
  writeback_pending: 'Write-back pending',
  exception_open: 'Exception open',
  reprint_required: 'Reprint required',
  first_order: 'First order',
  safearrival: 'SafeArrival',
  manual_order: 'Manual order',
  customer_replied: 'Customer replied',
  in_transit: 'In transit',
};

/**
 * `order_flags.detail` is jsonb, so a flag's reason is whatever the writer put there.
 *
 * The API flattens it to a string now (`repo/detail.ts#flagReason`), and this is the second
 * belt: a payload that still carries an object must not take the whole drawer down with
 * "objects are not valid as a React child". The screen degrades to the flag without its
 * reason, which is what a reader needs least.
 */
function reasonText(reason: unknown): string | null {
  if (typeof reason === 'string') return reason.trim() === '' ? null : reason;
  if (typeof reason === 'number' || typeof reason === 'boolean') return String(reason);
  return null;
}

/**
 * Flags that are facts about the order rather than problems with it.
 *
 * `first_order` and `safearrival` are already badges in the header and `in_transit` is
 * already the fulfilment state. Repeating them here as warnings is how a section that
 * should say "nothing is wrong" ends up saying "three flags" on every healthy order, which
 * is how the section stops being read.
 */
const NOT_A_PROBLEM: ReadonlySet<string> = new Set([
  'first_order',
  'safearrival',
  'manual_order',
  'in_transit',
]);

export function FlagsSection({
  detail,
  now,
  actions,
}: {
  detail: OrderDrawerDetail;
  now: number;
  actions?: ReactNode;
}) {
  const flags = detail.flags.filter((flag) => !NOT_A_PROBLEM.has(flag.flag));

  return (
    <Section title="Flags" id="flags" action={actions}>
      {flags.length === 0 ? (
        <p className="ord-sub">No flags on this order.</p>
      ) : (
        <ul className="ord-flags" role="list">
          {flags.map((flag) => (
            <li key={`${flag.flag}-${flag.since}`}>
              <Pill tone="warn">{FLAG_WORD[flag.flag] ?? flag.flag}</Pill>
              <span className="ord-sub" title={fullTimestamp(flag.since)}>
                since {relativeAge(flag.since, now)}
              </span>
              {reasonText(flag.reason) ? (
                <span className="ord-note">{reasonText(flag.reason)}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {detail.support_ticket_refs && detail.support_ticket_refs.length > 0 ? (
        <Fact label="Support">
          {detail.support_ticket_refs.map((ticket) => (
            <span key={ticket.public_ref} className="ord-badge">
              {ticket.public_ref} {ticket.status}
            </span>
          ))}
        </Fact>
      ) : null}
    </Section>
  );
}
